import {state} from '@bitstillery/mithril'
import {Button, Dialog, Icon} from '@bitstillery/common/components'
import {MithrilComponent} from '@bitstillery/mithril'
import {logger} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

export interface VersionCheckerAttrs {
    title: string
    reloadButton: string
}

export class VersionChecker extends MithrilComponent<VersionCheckerAttrs> {
    static timeout = 60_000
    static seconds_to_wait_reload = 60

    check_interval_handle = 0
    count_interval_handle: number | null = null
    check_paused_count = 0 // Only check if this == 0

    data = state(
        {
            modal: {
                visible: false,
            },
            reload: {
                count: VersionChecker.seconds_to_wait_reload,
            },
            version: {
                current: process.env.MSI_VERSION,
                isNew: false,
                latest: process.env.MSI_VERSION,
            },
        },
        'version-checker-data',
    )

    override async oncreate() {
        if (
            process.env.MSI_TEST_CHECK_VERSION ||
            (process.env.BUN_ENV && ['staging', 'production'].includes(process.env.BUN_ENV))
        ) {
            this.check_interval_handle = window.setInterval(() => {
                void this.check_version()
            }, VersionChecker.timeout)
            this.data.version.current = await this.fetch_current_version()
            this.data.version.latest = this.data.version.current
        }
    }

    async check_version() {
        // If the version-checking is paused, decrease the pause-counter
        if (this.check_paused_count > 0) {
            this.check_paused_count--
            return
        }
        // If the dialog is visable, do noop
        if (this.data.modal.visible) {
            return
        }

        try {
            this.data.version.latest = await this.fetch_current_version()

            if (this.data.version.current !== this.data.version.latest) {
                if (!this.data.version.isNew) {
                    this.start_reload_count()
                }
                this.data.version.isNew = true
            }
        } catch (error) {
            logger.error(error instanceof Error ? error.message : String(error))
        }
    }

    async fetch_current_version(): Promise<string> {
        const request = new Request('/version.json', {
            cache: 'no-cache',
            headers: {
                Accept: 'application/json, text/*',
                'Accept-Encoding': 'gzip, deflate, br',
                'Content-Type': 'application/json; charset=UTF-8',
            },
            method: 'GET',
        })

        let response

        try {
            response = await fetch(request, {})
            if (response.status >= 200 && response.status < 300) {
                // The build writes the version as a bare JSON string.
                const version: unknown = await response.json()
                return typeof version === 'string' ? version : this.data.version.latest
            }
            // Response status 0: fetch was interrupted (browser slept, user navigated, firewall, plugins, ...)
            return this.data.version.latest
        } catch {
            // Swallow exceptions. If there is a problem with this GET, there are other issues than reloading the site.
            return this.data.version.latest
        }
    }

    start_reload_count() {
        this.data.modal.visible = true
        this.count_interval_handle = window.setInterval(() => {
            this.data.reload.count -= 1
            if (this.data.reload.count === 0 && this.count_interval_handle) {
                this.clear_count_interval()
                window.location.reload()
            }
        }, 1000)
    }

    clear_count_interval() {
        if (this.count_interval_handle) {
            clearInterval(this.count_interval_handle)
        }
    }

    abort_update() {
        this.clear_count_interval()
        this.data.modal.visible = false

        this.check_paused_count = 30 // Wait this many ticks before the version check is done again
        this.data.reload.count = VersionChecker.seconds_to_wait_reload // Reset the reload count
    }

    view(vn: m.Vnode<VersionCheckerAttrs>): m.Children {
        return (
            <div className='c-version-checker'>
                {this.data.version.isNew && this.data.modal.visible && (
                    <Dialog
                        title={`${vn.attrs.title} (${this.data.version.current} => ${this.data.version.latest})`}
                        onclose={() => this.abort_update()}
                    >
                        <div className='info-box'>
                            <div className='icon-construction'>
                                <Icon name='party' className='icon-xl' />
                            </div>
                            <div class='text'>
                                {vn.children}
                                {'This page will automatically reload in '}
                                <b>{`${this.data.reload.count}`}</b> {' seconds. '}
                            </div>
                        </div>

                        <Button
                            icon='repeat'
                            className='btn-confirm'
                            onclick={() => window.location.reload()}
                            text={vn.attrs.reloadButton}
                            type='info'
                        />
                    </Dialog>
                )}
            </div>
        )
    }
}
